import { TreeKeyManagerItem } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { StateService } from '@uirouter/core';

import { CompanyTreeRow } from 'src/app/app-common/types/company-tree';
import { ComponentsModule } from 'src/app/components/components.module';

const INDENT_PER_LEVEL_PX = 32;

@Component({
  selector: 'div[company-tree-row]',
  templateUrl: './company-tree-row.component.html',
  styleUrl: './company-tree-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ComponentsModule, MatIconModule],
  host: {
    class: 'company-tree-row',
    '[class.company-tree-row--selected]': 'showSelectedStyle()',
    role: 'treeitem',
    '[attr.tabindex]': 'tabIndex()',
    '[attr.aria-level]': 'treeAriaLevel()',
    '[attr.aria-posinset]': 'treeAriaPosInSet()',
    '[attr.aria-setsize]': 'treeAriaSetSize()',
    '[attr.aria-expanded]': 'ariaExpanded()',
    '[attr.aria-current]': 'ariaCurrent()',
    '[attr.aria-label]': 'accessibleName()',
    '(click)': 'onClick($event)',
    '(keydown)': 'onKeydown($event)',
  },
})
export class CompanyTreeRowComponent implements TreeKeyManagerItem {
  private readonly stateService = inject(StateService);
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly row = input.required<CompanyTreeRow>();

  readonly selectCompany = output<string>();
  readonly toggleExpand = output<string>();
  readonly retry = output<string>();
  readonly revealChildren = output<string>();
  readonly hideChildren = output<string>();

  private readonly focusableState = signal(false);
  private parentRow: CompanyTreeRowComponent | null = null;
  private childRows: CompanyTreeRowComponent[] = [];

  protected readonly isFocusable = this.focusableState.asReadonly();

  protected readonly isSearch = computed(() => this.row().variant === 'search');

  protected readonly tabIndex = computed(() => (this.isFocusable() ? 0 : -1));

  protected readonly treeAriaLevel = computed(() => this.row().level);

  protected readonly treeAriaPosInSet = computed(() => this.row().posInSet);

  protected readonly treeAriaSetSize = computed(() => this.row().setSize);

  protected readonly ariaCurrent = computed(() =>
    this.row().selected ? 'true' : null,
  );

  protected readonly showSelectedStyle = computed(() => this.row().selected);

  protected readonly parentLine = computed(() => {
    if (!this.isSearch()) {
      return null;
    }

    const parentName = this.row().parentName?.trim();
    return parentName ? `in ${parentName}` : null;
  });

  protected readonly showRevealControl = computed(
    () => this.row().hiddenChildren,
  );

  protected readonly showHideControl = computed(
    () => this.row().canHideChildren,
  );

  protected readonly showExpandControl = computed(() => {
    const row = this.row();
    return (
      (row.variant === 'branch' || row.variant === 'search') &&
      row.expandable &&
      !row.hiddenChildren
    );
  });

  /**
   * Every level below the root reserves one indentation step, and the row's own
   * expand control sits in the last of them. Rows without a control pad that
   * step out instead, so a leaf stays aligned with its expandable siblings.
   *
   * A search row reserves one step more than a tree row at the same level: the
   * search tree's first level carries expand controls, unlike the tree's first
   * level (the root), which never does. A branch-view row reserves the same
   * extra step for the whole opening (`reserveRootControlStep`), so the root
   * can carry "…" without any row shifting once the control goes away.
   */
  protected readonly indentWidth = computed(() => {
    const row = this.row();
    const steps =
      this.isSearch() || row.reserveRootControlStep
        ? row.level
        : Math.max(row.level - 1, 0);
    const control =
      this.showExpandControl() ||
      this.showRevealControl() ||
      this.showHideControl()
        ? 1
        : 0;
    return Math.max(steps - control, 0) * INDENT_PER_LEVEL_PX;
  });

  protected readonly showError = computed(() => !!this.row().errorMessage);

  protected readonly showSpinner = computed(
    () => this.row().loading && !this.showError(),
  );

  protected readonly showCheck = computed(
    () => this.row().selected && !this.showError() && !this.showSpinner(),
  );

  protected readonly showDot = computed(() => {
    const row = this.row();
    return (
      row.containsSelected &&
      !row.expanded &&
      !row.selected &&
      !this.showError() &&
      !this.showSpinner()
    );
  });

  protected href(): string {
    const companyId = this.row().id;
    const current = this.stateService.current?.name;

    return current
      ? this.stateService.href(current, {
          ...this.stateService.params,
          cid: companyId,
        })
      : this.stateService.href('apps.home', { cid: companyId });
  }

  /**
   * A non-empty `aria-label` replaces the row's descendant text, so the label
   * has to carry everything the row shows — name, address and parent line —
   * before the row state.
   */
  protected accessibleName(): string {
    const row = this.row();
    const parts = [];

    for (const part of [row.name?.trim(), row.address, this.parentLine()]) {
      if (part) {
        parts.push(part);
      }
    }

    if (row.selected) {
      parts.push('selected');
    }

    if (this.showDot()) {
      parts.push('contains selected company');
    }

    if (row.hiddenChildren) {
      parts.push('some sub-companies hidden, press Right Arrow to show them');
    }

    if (row.canHideChildren) {
      parts.push('other sub-companies shown, press Left Arrow to hide them');
    }

    if (this.showError()) {
      parts.push('failed to load, press Alt+Enter to retry');
    }

    return parts.join(', ');
  }

  protected ariaExpanded(): boolean | null {
    const row = this.row();
    if (row.variant === 'root') {
      return true;
    }

    return row.expandable ? row.expanded : null;
  }

  protected onClick(event: MouseEvent): void {
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    this.selectCompany.emit(this.row().id);
  }

  protected onToggle(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.toggleExpand.emit(this.row().id);
  }

  protected onReveal(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.revealChildren.emit(this.row().id);
  }

  protected onHide(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.hideChildren.emit(this.row().id);
  }

  setTreeLinks(
    parent: CompanyTreeRowComponent | null,
    children: CompanyTreeRowComponent[],
  ): void {
    this.parentRow = parent;
    this.childRows = children;
  }

  getParent(): TreeKeyManagerItem | null {
    return this.parentRow;
  }

  getChildren(): TreeKeyManagerItem[] {
    return this.childRows;
  }

  isExpanded = (): boolean =>
    !this.row().hiddenChildren &&
    (this.row().variant === 'root' || this.row().expanded);

  getLabel(): string {
    return this.row().name;
  }

  activate(): void {
    this.selectCompany.emit(this.row().id);
  }

  expand(): void {
    const row = this.row();
    // Checked before the root guard, or the root's "…" would have no
    // keyboard path at all and the change would fail WCAG 2.1.1.
    if (row.hiddenChildren) {
      this.revealChildren.emit(row.id);
      return;
    }

    if (row.variant === 'root' || !row.expandable || row.expanded) {
      return;
    }

    this.toggleExpand.emit(row.id);
  }

  collapse(): void {
    const row = this.row();
    // Checked before the root guard, or the root's down arrow would have no
    // keyboard path at all and the change would fail WCAG 2.1.1.
    if (row.canHideChildren) {
      this.hideChildren.emit(row.id);
      return;
    }

    // Nothing is collapsible on screen until the reveal.
    if (row.variant === 'root' || !row.expanded || row.hiddenChildren) {
      return;
    }

    this.toggleExpand.emit(row.id);
  }

  focus(): void {
    this.focusableState.set(true);
    const element = this.hostEl.nativeElement;
    const tree = element.closest('[role="tree"]');
    const active = element.ownerDocument.activeElement;
    if (tree && active && tree.contains(active)) {
      element.focus();
    }
  }

  unfocus(): void {
    this.focusableState.set(false);
  }

  makeFocusable(): void {
    this.focusableState.set(true);
  }

  protected onRetry(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.retry.emit(this.row().id);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this.showError() && event.key === 'Enter' && event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      this.retry.emit(this.row().id);
      return;
    }

    if (
      event.key === 'Enter' &&
      (event.ctrlKey || event.metaKey) &&
      !event.altKey &&
      !event.shiftKey
    ) {
      event.preventDefault();
      window.open(this.href(), '_blank');
    }
  }
}
